/* PDP add-on selector: selection state, running total, and combined add-to-cart.
   Reuses the theme's items[] -> cart_add mechanism; add-ons carry a _addon_discount property. */
if (!customElements.get('addon-selector')) {
  class ProductAddons extends HTMLElement {
    constructor() {
      super();
      this.onChange = this.onChange.bind(this);
      this.onSubmitCapture = this.onSubmitCapture.bind(this);
      this.recalc = this.recalc.bind(this);
    }

    connectedCallback() {
      this.sectionId = this.dataset.sectionId || '';
      this.moneyFormat = this.dataset.moneyFormat || '';
      this.noneText = this.dataset.noneText || '';
      this.errorText = this.dataset.errorText || '';
      // Base price (cents) from Liquid: reliable on every template, unlike the [data-selected-variant]
      // DOM blob which some product templates don't emit. Kept live by the variantChange payload below.
      this.mainPrice = parseInt(this.dataset.mainPrice, 10);
      if (isNaN(this.mainPrice)) this.mainPrice = 0;
      this.baseSubmitText = '';
      this.formBindRetries = 0;
      this.totalRetries = 0;

      this.addEventListener('change', this.onChange);
      if (window.FoxTheme && FoxTheme.pubsub) {
        this.variantUnsub = FoxTheme.pubsub.subscribe(FoxTheme.pubsub.PUB_SUB_EVENTS.variantChange, this.recalc);
      }

      this.groups.forEach((group) => this.updateGroupLabel(group));
      // The add-on block renders before the buy-buttons form, so bind once that form exists.
      this.bindForm();
    }

    disconnectedCallback() {
      this.removeEventListener('change', this.onChange);
      if (this.formBound && this.form) this.form.removeEventListener('submit', this.onSubmitCapture, true);
      if (this.variantUnsub) this.variantUnsub();
    }

    resolveForm() {
      // Re-resolve only when there's no form yet or the cached one was detached by a re-render, then rebind the listener to the live form.
      if (this.form && document.contains(this.form)) return;
      const section = this.closest('[id^="shopify-section-"]') || document;
      const nextForm =
        section.querySelector('#product-form-' + this.sectionId) ||
        section.querySelector('form.main-product-form') ||
        document.querySelector('form.main-product-form');
      if (nextForm === this.form) return;
      if (this.form) this.form.removeEventListener('submit', this.onSubmitCapture, true);
      this.form = nextForm;
      this.submitButton = this.form && this.form.querySelector('.product-form__submit');
      this.submitTextEl = this.submitButton && this.submitButton.querySelector('.btn__text');
      if (this.submitTextEl && !this.baseSubmitText) this.baseSubmitText = this.submitTextEl.textContent.trim();
      if (this.form) {
        this.form.addEventListener('submit', this.onSubmitCapture, true);
        this.formBound = true;
      }
    }

    bindForm() {
      this.resolveForm();
      if (this.form) {
        this.updateTotal();
      } else if (this.formBindRetries < 200) {
        this.formBindRetries += 1;
        requestAnimationFrame(() => this.bindForm());
      }
    }

    get groups() {
      return Array.from(this.querySelectorAll('[data-addon-group]'));
    }

    recalc(payload) {
      // Keep the base price live when the shopper switches variant (pubsub payload carries it in cents).
      const variant = payload && payload.data && payload.data.variant;
      if (variant && typeof variant.price === 'number') this.mainPrice = variant.price;
      this.updateTotal();
    }

    onChange(event) {
      if (event.target.matches('[data-addon-variant-select]')) {
        this.updateRowPrice(event.target);
        this.updateTotal();
        return;
      }
      if (!event.target.matches('[data-addon-input]')) return;
      const group = event.target.closest('[data-addon-group]');
      if (group) this.updateGroupLabel(group);
      this.updateTotal();
    }

    // Reflect the chosen variant's price in the row, so the shown price tracks the dropdown.
    updateRowPrice(select) {
      const row = select.closest('.product-addon-row');
      const priceNow = row && row.querySelector('.product-addon-row__price-now');
      if (!priceNow) return;
      const group = select.closest('[data-addon-group]');
      const incentive = parseFloat(group && group.dataset.addonIncentive) || 0;
      const chosen = select.selectedOptions[0];
      const price = parseInt(chosen && chosen.dataset.price, 10) || 0;
      const formatted = this.formatMoney(Math.round(price * (1 - incentive / 100)));
      if (formatted) priceNow.textContent = formatted;
    }

    selectedInputs(scope) {
      const root = scope || this;
      return Array.from(root.querySelectorAll('[data-addon-input]')).filter(
        (input) => input.checked && input.dataset.addonVariant
      );
    }

    updateGroupLabel(group) {
      const label = group.querySelector('[data-addon-selected-label]');
      if (!label) return;
      const chosen = this.selectedInputs(group);
      if (!chosen.length) {
        label.textContent = this.noneText;
        return;
      }
      const names = chosen.map((input) => input.dataset.addonName || '').filter(Boolean);
      label.textContent = names.join(', ');
    }

    mainVariantPrice() {
      if (this.mainPrice) return this.mainPrice;
      // Fallback for templates that expose the selected-variant JSON blob.
      const section = this.closest('[id^="shopify-section-"]') || document;
      const source =
        section.querySelector('#variant-selects-' + this.sectionId + ' [data-selected-variant]') ||
        section.querySelector('[data-selected-variant]') ||
        document.querySelector('[data-selected-variant]');
      if (!source) return 0;
      try {
        const variant = JSON.parse(source.textContent);
        return variant && typeof variant.price === 'number' ? variant.price : 0;
      } catch (error) {
        return 0;
      }
    }

    // Resolve the chosen variant + price for an option, preferring its variant dropdown (rows with
    // multiple variants) over the input's default-variant data attributes.
    variantOf(input) {
      const option = input.closest('.product-addon-row, .product-addon-card');
      const select = option && option.querySelector('[data-addon-variant-select]');
      if (select && select.value) {
        const chosen = select.selectedOptions[0];
        return { id: select.value, price: parseInt(chosen && chosen.dataset.price, 10) || 0 };
      }
      return { id: input.dataset.addonVariant, price: parseInt(input.dataset.addonPrice, 10) || 0 };
    }

    addonsExtra() {
      let extra = 0;
      this.groups.forEach((group) => {
        const incentive = parseFloat(group.dataset.addonIncentive) || 0;
        this.selectedInputs(group).forEach((input) => {
          const price = this.variantOf(input).price;
          extra += Math.round(price * (1 - incentive / 100));
        });
      });
      return extra;
    }

    formatMoney(cents) {
      if (window.FoxTheme && FoxTheme.Currency) return FoxTheme.Currency.formatMoney(cents, this.moneyFormat);
      return '';
    }

    updateTotal() {
      this.resolveForm();
      const total = this.mainVariantPrice() + this.addonsExtra();
      const formatted = this.formatMoney(total);
      // FoxTheme.Currency can load after the first bind; retry (frame-capped) so the base price still lands.
      if (!formatted && this.totalRetries < 200) {
        this.totalRetries += 1;
        requestAnimationFrame(() => this.updateTotal());
        return;
      }
      if (this.submitTextEl && this.baseSubmitText && formatted) {
        this.submitTextEl.textContent = this.baseSubmitText + ' · ' + formatted;
      }
    }

    onSubmitCapture(event) {
      const chosen = this.selectedInputs();
      if (!chosen.length) return; // No add-ons: let the theme's normal single-item add proceed.
      event.preventDefault();
      event.stopImmediatePropagation();
      this.addCombined(chosen);
    }

    buildItems(chosen) {
      const mainIdInput = this.form && this.form.querySelector('[name="id"]');
      const qtyInput = this.form && this.form.querySelector('[name="quantity"]');
      const quantity = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;
      const mainId = mainIdInput && mainIdInput.value ? parseInt(mainIdInput.value, 10) : null;
      const items = [];
      if (mainId) {
        const mainItem = { id: mainId, quantity: quantity };
        // Carry the main form's subscription plan and line-item properties (gift-card recipient, engraving) so an add-on selection can't silently drop them.
        const sellingPlan = this.form && this.form.querySelector('[name="selling_plan"]');
        if (sellingPlan && sellingPlan.value) mainItem.selling_plan = parseInt(sellingPlan.value, 10);
        const properties = {};
        if (this.form) {
          this.form.querySelectorAll('[name^="properties["]').forEach((field) => {
            if ((field.type === 'checkbox' || field.type === 'radio') && !field.checked) return;
            if (field.type === 'file') return; // JSON cart add can't carry an upload; skip so we don't send the fakepath string
            const key = field.name.slice(11, -1);
            if (key && field.value) properties[key] = field.value;
          });
        }
        if (Object.keys(properties).length) mainItem.properties = properties;
        items.push(mainItem);
      }
      chosen.forEach((input) => {
        const id = parseInt(this.variantOf(input).id, 10);
        if (isNaN(id)) return;
        const group = input.closest('[data-addon-group]');
        const incentive = parseFloat(group && group.dataset.addonIncentive) || 0;
        const properties = { _addon_for: mainId ? String(mainId) : '' };
        if (incentive > 0) properties._addon_discount = String(incentive);
        items.push({ id: id, quantity: 1, properties: properties });
      });
      return items;
    }

    async addCombined(chosen) {
      if (this.loading) return;
      this.loading = true;
      this.showError('');
      if (this.submitButton) {
        this.submitButton.setAttribute('aria-disabled', 'true');
        this.submitButton.classList.add('btn--loading');
      }
      try {
        const utils = window.FoxTheme && FoxTheme.utils;
        const routes = (window.FoxTheme && FoxTheme.routes) || {};
        const config = utils && typeof utils.fetchConfig === 'function' ? utils.fetchConfig('javascript') : { headers: {} };
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        // Collect the cart sections (drawer, icon) so their HTML re-renders on cartUpdate, exactly as
        // the theme's own add-to-cart does. Without this the drawer can't repaint and its subscriber
        // throws on the missing sections, surfacing a misleading "error" even though the add succeeded.
        const sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );
        config.body = JSON.stringify({ items: this.buildItems(chosen), sections: sectionsToBundle, sections_url: window.location.pathname });
        const response = await fetch(routes.cart_add_url || '/cart/add.js', config);
        const parsed = await response.json();
        if (parsed.status) {
          this.showError(parsed.description || parsed.message);
          return;
        }
        const cart = await (await fetch(routes.cart_url || '/cart.js', utils && typeof utils.fetchConfig === 'function' ? utils.fetchConfig() : {})).json();
        cart.sections = parsed.sections || {};
        if (window.FoxTheme && FoxTheme.pubsub) FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cart });
        document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: parsed } }));
        this.reset();
        this.openDrawer();
      } catch (error) {
        console.warn('Add-on combined add failed', error);
        this.showError(this.errorText);
      } finally {
        this.loading = false;
        if (this.submitButton) {
          this.submitButton.classList.remove('btn--loading');
          this.submitButton.removeAttribute('aria-disabled');
        }
      }
    }

    openDrawer() {
      const drawer = document.querySelector('cart-drawer');
      if (drawer && typeof drawer.show === 'function') drawer.show(this.submitButton);
    }

    reset() {
      this.groups.forEach((group) => {
        const none = group.querySelector('.product-addon-card--none .product-addon-card__input');
        if (none) none.checked = true;
        group.querySelectorAll('input[type="checkbox"][data-addon-input]').forEach((checkbox) => {
          checkbox.checked = false;
        });
        this.updateGroupLabel(group);
      });
      this.updateTotal();
    }

    showError(message) {
      const errorEl = this.form && this.form.querySelector('.product-form__error-message');
      if (!errorEl) return;
      errorEl.hidden = !message;
      errorEl.textContent = message || '';
    }
  }
  customElements.define('addon-selector', ProductAddons);
}
